/**
 * Shared PostHog analytics contract — event names + canonical flow ids.
 *
 * Both the Express server and the React frontend import these via
 * `@shared/schemas/analytics`. A rename on one side would silently break the
 * other (PostHog just records a differently-named event — no loud error), so
 * per CLAUDE.md's shared-schema rule these constants are the single source of
 * truth, replacing the previously-duplicated `src/config/analytics.ts` and
 * `server/src/config/analytics.ts`.
 */

/**
 * Canonical flow identifier — the stable value of the `flow` property carried
 * on every generation event. One id per user-facing generation flow; used for
 * all "by flow" breakdowns in PostHog. Kebab-case.
 */
export const FLOW_IDS = {
  IMAGE_GENERATOR: "image-generator",
  TEXT_TO_VIDEO: "text-to-video",
  FRAMES_TO_VIDEO: "frames-to-video",
  PRODUCT_CATALOG: "product-catalog",
  IMAGE_DIRECTOR: "image-director",
  PROFILE_PHOTO: "profile-photo",
  PRODUCT_CREATIVE: "product-creative",
  TALKING_AVATAR: "talking-avatar",
  BASEBALL_TREND: "baseball-trend",
} as const;

export type FlowId = (typeof FLOW_IDS)[keyof typeof FLOW_IDS];

/**
 * Maps DB `pipeline` / `generation_type` column values to the canonical flow
 * id. Used where only the stored pipeline string is available — e.g. gallery
 * lightboxes, and server generation services keyed by `GenerationType`.
 */
export const PIPELINE_TO_FLOW: Record<string, FlowId> = {
  edit: FLOW_IDS.IMAGE_GENERATOR,
  ad: FLOW_IDS.PRODUCT_CATALOG,
  director: FLOW_IDS.IMAGE_DIRECTOR,
  "text-to-video": FLOW_IDS.TEXT_TO_VIDEO,
  "frames-to-video": FLOW_IDS.FRAMES_TO_VIDEO,
  profile_photo: FLOW_IDS.PROFILE_PHOTO,
  product_creative: FLOW_IDS.PRODUCT_CREATIVE,
  talking_avatar: FLOW_IDS.TALKING_AVATAR,
  baseball_trend: FLOW_IDS.BASEBALL_TREND,
};

/** Resolve a stored `pipeline` / `generation_type` string to its flow id. */
export function flowFromPipeline(
  pipeline: string | null | undefined,
): FlowId | undefined {
  if (!pipeline) return undefined;
  return PIPELINE_TO_FLOW[pipeline];
}

/** Lifecycle phase of a generation — the `generation_*` server event family. */
export const GENERATION_PHASE = {
  STARTED: "started",
  COMPLETED: "completed",
  FAILED: "failed",
} as const;
export type GenerationPhase =
  (typeof GENERATION_PHASE)[keyof typeof GENERATION_PHASE];

/**
 * Media a generation flow produces — the `media_type` event property.
 * Values match `MediaType` in `./models.ts`; import that type when one is
 * needed (not re-declared here to avoid an `export *` name clash).
 */
export const MEDIA_TYPE = {
  IMAGE: "image",
  VIDEO: "video",
} as const;

/**
 * Every PostHog event name the app emits. `[s]` = captured server-side (fires
 * regardless of cookie consent), `[c]` = captured client-side (consent-gated,
 * routed through the pre-consent queue).
 *
 * The generation lifecycle (`generation_started` / `_completed` / `_failed`)
 * is server-side so the core funnel is never lost to consent opt-out.
 */
export const ANALYTICS_EVENTS = {
  // ── Auth ────────────────────────────────────────────────────────────
  USER_SIGN_UP: "user_sign_up", // [s] authoritative — /api/auth/track-signup
  USER_SIGN_IN: "user_sign_in", // [c]
  USER_SIGN_OUT: "user_sign_out", // [c]

  // ── Generation lifecycle — unified, server-side ─────────────────────
  GENERATION_STARTED: "generation_started", // [s]
  GENERATION_COMPLETED: "generation_completed", // [s]
  GENERATION_FAILED: "generation_failed", // [s]

  // ── Generation engagement ───────────────────────────────────────────
  TOOL_PAGE_VIEW: "tool_page_view", // [c]
  IMAGE_UPLOAD: "image_upload", // [c]
  PROMPT_ENHANCE_CLICK: "prompt_enhance_click", // [c]
  PROMPT_IMPROVE: "prompt_improve", // [s]
  TEMPLATE_SELECTED: "template_selected", // [c] — user clicked a preset/template tile
  FLOW_MODE_SELECTED: "flow_mode_selected", // [c] — user picked a path inside a flow (carries `flow` + `mode`)

  // ── Pipeline health (server-side observability) ─────────────────────
  MODEL_INVOCATION_STARTED: "model_invocation_started", // [s]
  MODEL_INVOCATION_COMPLETED: "model_invocation_completed", // [s]
  MODEL_INVOCATION_FAILED: "model_invocation_failed", // [s]
  R2_UPLOAD_COMPLETED: "r2_upload_completed", // [s]
  R2_UPLOAD_FAILED: "r2_upload_failed", // [s]

  // ── Credits ─────────────────────────────────────────────────────────
  CREDITS_DEDUCT: "credits_deduct", // [s]
  CREDITS_INSUFFICIENT: "credits_insufficient", // [s]
  CREDITS_REFUNDED: "credits_refunded", // [s] — bulk or per-image refund succeeded
  CREDITS_REFUND_FAILED: "credits_refund_failed", // [s] — refund RPC errored; user lost credits
  CREDITS_INSUFFICIENT_SHOWN: "credits_insufficient_shown", // [c] — upsell modal opened

  // ── Content engagement ──────────────────────────────────────────────
  CONTENT_DOWNLOAD: "content_download", // [c]
  CONTENT_SHARE: "content_share", // [c]
  GALLERY_VIEW: "gallery_view", // [c]

  // ── Pricing & checkout ──────────────────────────────────────────────
  PRICING_MODAL_VIEW: "pricing_modal_view", // [c]
  PRICING_PLAN_CLICK: "pricing_plan_click", // [c]
  PAYMENT_INITIATED: "payment_initiated", // [c]
  PAYMENT_REDIRECT: "payment_redirect", // [c]
  CHECKOUT_VIEWED: "checkout_viewed", // [c]
  PAYMENT_RESULT_VIEWED: "payment_result_viewed", // [c]
  PAYMENT_VERIFIED: "payment_verified", // [c]
  PAYMENT_FAILED_VIEWED: "payment_failed_viewed", // [c]
  PAYMENT_CREATION_FAILED: "payment_creation_failed", // [c]
  PAYMENT_VERIFICATION_TIMEOUT: "payment_verification_timeout", // [c]

  // ── Onboarding ──────────────────────────────────────────────────────
  ONBOARDING_COMPLETE: "onboarding_complete", // [c]

  // ── Plan changes ────────────────────────────────────────────────────
  UPGRADE_CONFIRMED: "upgrade_confirmed", // [c]
  DOWNGRADE_CONFIRMED: "downgrade_confirmed", // [c] + [s]
  REACTIVATION_CONFIRMED: "reactivation_confirmed", // [c]
  REACTIVATION_SUCCEEDED: "reactivation_succeeded", // [c]
  REACTIVATION_FAILED: "reactivation_failed", // [c]
  SCHEDULED_CHANGE_CANCELLED: "scheduled_change_cancelled", // [c]

  // ── Cancellation funnel ─────────────────────────────────────────────
  CANCELLATION_STARTED: "cancellation_started", // [c]
  CANCELLATION_REASON_SELECTED: "cancellation_reason_selected", // [c]
  CANCELLATION_DISCOUNT_OFFERED: "cancellation_discount_offered", // [c]
  CANCELLATION_DISCOUNT_ACCEPTED: "cancellation_discount_accepted", // [c]
  CANCELLATION_DISCOUNT_DECLINED: "cancellation_discount_declined", // [c]
  CANCELLATION_CONFIRMED: "cancellation_confirmed", // [c]
  CANCELLATION_ERROR: "cancellation_error", // [c]

  // ── Coupon ──────────────────────────────────────────────────────────
  COUPON_APPLIED: "coupon_applied", // [c]
  COUPON_VALID: "coupon_valid", // [c]
  COUPON_INVALID: "coupon_invalid", // [c]
  COUPON_VALIDATED: "coupon_validated", // [s]
  COUPON_VALIDATION_FAILED: "coupon_validation_failed", // [s]

  // ── Payment & subscription — server-side ────────────────────────────
  SUBSCRIPTION_ACTIVATED: "subscription_activated",
  TOKEN_PACK_PURCHASED: "token_pack_purchased",
  PAYMENT_LINK_GENERATED: "payment_link_generated",
  PAYMENT_FAILED: "payment_failed",
  SUBSCRIPTION_CANCELLED: "subscription_cancelled",
  SUBSCRIPTION_EXPIRED: "subscription_expired",
  SUBSCRIPTION_DOWNGRADED_TO_FREE: "subscription_downgraded_to_free",
  SUBSCRIPTION_REACTIVATED: "subscription_reactivated",
  SUBSCRIPTION_PAST_DUE: "subscription_past_due",
  SUBSCRIPTION_PAST_DUE_AUTO_CANCELLED: "subscription_past_due_auto_cancelled",
  SUBSCRIPTION_RENEWED: "subscription_renewed",
  CREDITS_MONTHLY_REFRESH: "credits_monthly_refresh",
  SUBSCRIPTION_DISCOUNT_APPLIED: "subscription_discount_applied",
  DISCOUNT_RESTORED: "discount_restored",
  DISCOUNT_RESTORE_FAILED: "discount_restore_failed",

  // ── Cron batch metrics — server-side ────────────────────────────────
  CRON_RENEWAL_COMPLETED: "cron_renewal_completed",
  CRON_EXPIRY_COMPLETED: "cron_expiry_completed",
  CRON_CREDITS_REFRESH_COMPLETED: "cron_credits_refresh_completed",
  CRON_DISCOUNT_RESTORE_COMPLETED: "cron_discount_restore_completed",
  CRON_PURCHASED_CREDITS_EXPIRY_COMPLETED:
    "cron_purchased_credits_expiry_completed",
  CRON_PENDING_DELETES_COMPLETED: "cron_pending_deletes_completed",

  // ── Referral ────────────────────────────────────────────────────────
  REFERRAL_LANDING_VIEW: "referral_landing_view", // [c]
  REFERRAL_LINK_COPIED: "referral_link_copied", // [c]
  REFERRAL_LINK_SHARED: "referral_link_shared", // [c]
  REFERRAL_ATTRIBUTED: "referral_attributed", // [s]
  REFERRAL_REWARD_EARNED: "referral_reward_earned", // [s] — referrer rewarded on friend's first paid subscription
  REFERRAL_BONUS_EARNED: "referral_bonus_earned", // [s] — referee bonus granted on their first paid subscription

  // ── Community (WhatsApp group) ──────────────────────────────────────
  COMMUNITY_CTA_VIEW: "community_cta_view", // [c] — join card/link rendered; carries `source`
  COMMUNITY_CTA_CLICK: "community_cta_click", // [c] — "Join" clicked; carries `source`
  COMMUNITY_MODAL_SHOWN: "community_modal_shown", // [c] — first-generation prompt opened
  COMMUNITY_MODAL_DISMISSED: "community_modal_dismissed", // [c] — first-generation prompt dismissed
  SUPPORT_REPORT_CLICK: "support_report_click", // [c] — "Report on WhatsApp" tapped in an error toast

  // ── Feature requests ────────────────────────────────────────────────
  FEATURE_REQUEST_DIALOG_OPENED: "feature_request_dialog_opened", // [c]
  FEATURE_REQUEST_DIALOG_DISMISSED: "feature_request_dialog_dismissed", // [c]
  FEATURE_REQUEST_SUBMITTED: "feature_request_submitted", // [c]
  FEATURE_REQUEST_ADMIN_STATUS_CHANGED: "feature_request_admin_status_changed", // [c]

  // ── Bot lifecycle — server-side ─────────────────────────────────────
  // Owner CRUD on /api/bots — all distinct-id'd by the owner userId.
  BOT_CREATED: "bot_created", // [s]
  BOT_CREATE_FAILED: "bot_create_failed", // [s]
  BOT_UPDATED: "bot_updated", // [s]
  BOT_UPDATE_FAILED: "bot_update_failed", // [s]
  BOT_DELETED: "bot_deleted", // [s]
  BOT_DELETE_FAILED: "bot_delete_failed", // [s]
  BOT_DEPOSIT_COMPLETED: "bot_deposit_completed", // [s]
  BOT_DEPOSIT_FAILED: "bot_deposit_failed", // [s]
  // Bot Builder UI engagement
  BOT_BUILDER_OPENED: "bot_builder_opened", // [c]
  BOT_EMBED_CODE_COPIED: "bot_embed_code_copied", // [c]
  BOT_DEPOSIT_DIALOG_OPENED: "bot_deposit_dialog_opened", // [c]
  // Visitor + preview chat — distinct-id'd by owner userId; carry `surface` + `bot_id`.
  BOT_MESSAGE_STARTED: "bot_message_started", // [s]
  BOT_MESSAGE_COMPLETED: "bot_message_completed", // [s]
  BOT_MESSAGE_FAILED: "bot_message_failed", // [s]
  // Visitor was refused server-side (origin, plan gate, out of bot credits).
  BOT_VISITOR_BLOCKED: "bot_visitor_blocked", // [s]
} as const;

export type AnalyticsEvent =
  (typeof ANALYTICS_EVENTS)[keyof typeof ANALYTICS_EVENTS];
